/**
 * Keyboard utilities for shortcut handling and platform detection
 * 
 * This module provides cross-platform keyboard shortcut handling with proper
 * platform detection (Mac vs. Windows/Linux) and key combination parsing.
 * 
 * @module utils/keyboardUtils
 * 
 * @example
 * ```typescript
 * import { detectPlatform, getKeyCombination, formatKeyDisplay } from './keyboardUtils';
 * 
 * // Detect user's platform
 * const platform = detectPlatform(); // 'mac' | 'windows' | 'linux' | 'unknown'
 * 
 * // Handle keyboard event
 * document.addEventListener('keydown', (e) => {
 *   const combo = getKeyCombination(e);
 *   console.log('Pressed:', combo); // e.g., 'ctrl+shift+k'
 * });
 * ```
 */

import { Platform } from '../types/keyboard';
import { 
  PLATFORM_DETECTION, 
  KEY_DISPLAY_NAMES, 
  INPUT_ELEMENT_TAGS,
  BYPASS_INPUT_CHECK_KEYS 
} from '../constants/keyboardShortcuts';

/**
 * Interface for navigator.userAgentData (experimental API)
 */
interface NavigatorUAData {
  platform?: string;
}

/**
 * Extended Navigator interface with userAgentData property
 */
interface NavigatorWithUserAgentData extends Navigator {
  userAgentData?: NavigatorUAData;
}

// Cache platform detection result for performance
let cachedPlatform: Platform | null = null;

/**
 * Reset the cached platform (used for testing)
 * @internal
 */
export function resetPlatformCache(): void {
  cachedPlatform = null;
}

/**
 * Detect the current platform
 * 
 * Uses modern navigator.userAgentData when available, with fallback to
 * the older navigator.platform property for browsers that don't support it.
 * Result is cached for performance.
 * 
 * @returns The detected platform
 * 
 * @example
 * ```typescript
 * // Simple platform detection
 * const platform = detectPlatform();
 * 
 * if (platform === 'mac') {
 *   console.log('User is on macOS - show Cmd shortcuts');
 * } else if (platform === 'windows') {
 *   console.log('User is on Windows - show Ctrl shortcuts');
 * }
 * 
 * // Use in keyboard shortcut display
 * const modifier = platform === 'mac' ? '⌘' : 'Ctrl';
 * const shortcutText = `${modifier}+K to search`;
 * ```
 */
export function detectPlatform(): Platform {
  if (cachedPlatform !== null) {
    return cachedPlatform;
  }
  
  if (typeof window === 'undefined') {
    cachedPlatform = 'unknown';
    return cachedPlatform;
  }
  
  const nav = navigator as NavigatorWithUserAgentData;
  if ('userAgentData' in navigator && nav.userAgentData?.platform) {
    const platform = nav.userAgentData.platform.toUpperCase();
    
    if (platform.indexOf(PLATFORM_DETECTION.MAC) >= 0) {
      cachedPlatform = 'mac';
      return cachedPlatform;
    }
    if (platform.indexOf(PLATFORM_DETECTION.WINDOWS) >= 0) {
      cachedPlatform = 'windows';
      return cachedPlatform;
    }
    if (platform.indexOf(PLATFORM_DETECTION.LINUX) >= 0) {
      cachedPlatform = 'linux';
      return cachedPlatform;
    }
  }
  
  const platform = window.navigator.platform.toUpperCase();
  
  if (platform.indexOf(PLATFORM_DETECTION.MAC) >= 0) {
    cachedPlatform = 'mac';
    return cachedPlatform;
  }
  if (platform.indexOf(PLATFORM_DETECTION.WINDOWS) >= 0) {
    cachedPlatform = 'windows';
    return cachedPlatform;
  }
  if (platform.indexOf(PLATFORM_DETECTION.LINUX) >= 0) {
    cachedPlatform = 'linux';
    return cachedPlatform;
  }
  
  cachedPlatform = 'unknown';
  return cachedPlatform;
}

/**
 * Get platform modifier key (Cmd on Mac, Ctrl elsewhere)
 * 
 * @returns The platform-specific modifier key name
 * 
 * @example
 * ```typescript
 * // Get the right modifier for current platform
 * const modifier = getPlatformModifier();
 * 
 * // Use in shortcut configuration
 * const shortcuts = {
 *   search: `${modifier}+k`,    // 'cmd+k' on Mac, 'ctrl+k' elsewhere
 *   save: `${modifier}+s`,      // 'cmd+s' on Mac, 'ctrl+s' elsewhere
 * };
 * 
 * // Display in UI
 * const displayText = modifier === 'cmd' ? '⌘+K' : 'Ctrl+K';
 * ```
 */
export function getPlatformModifier(): 'cmd' | 'ctrl' {
  const platform = detectPlatform();
  return platform === 'mac' ? 'cmd' : 'ctrl';
}



/**
 * Get key combination string from keyboard event
 * 
 * Converts KeyboardEvent to a normalized key combination string.
 * Handles platform differences (Cmd vs. Ctrl) and special keys.
 * 
 * @param event - The keyboard event
 * @returns Key combination string (e.g., 'ctrl+shift+k')
 * 
 * @example
 * ```typescript
 * // In event handler
 * document.addEventListener('keydown', (event) => {
 *   const combo = getKeyCombination(event);
 *   
 *   // Match against shortcuts
 *   if (combo === 'ctrl+shift+k' || combo === 'cmd+shift+k') {
 *     event.preventDefault();
 *     openCommandPalette();
 *   }
 *   
 *   // Log for debugging
 *   console.log('Key combo:', combo);
 *   // Examples: 'ctrl+s', 'cmd+k', 'shift+enter', 'escape'
 * });
 * 
 * // Handle special keys
 * // Space key: 'ctrl+space'
 * // Escape key: 'escape'
 * // Enter key: 'shift+enter'
 * ```
 */
export function getKeyCombination(event: KeyboardEvent): string {
  const parts: string[] = [];
  const platform: Platform = detectPlatform();
  
  if (event.ctrlKey) parts.push('ctrl');
  if (event.metaKey) {
    if (platform === 'mac') {
      parts.push('cmd');
    } else {
      parts.push('meta');
    }
  }
  if (event.shiftKey) parts.push('shift');
  if (event.altKey) parts.push('alt');
  
  const key = event.key.toLowerCase();
  
  if (key === ' ') {
    parts.push('space');
  } else if (key === 'escape') {
    parts.push('escape');
  } else if (key === 'enter') {
    parts.push('enter');
  } else {
    parts.push(key);
  }
  
  return parts.join('+');
}

/**
 * Normalize shortcut string for comparison
 * 
 * Maintains the canonical modifier key order (ctrl, cmd, meta, shift, alt)
 * to ensure consistent matching with getKeyCombination output.
 * 
 * @param shortcut - The shortcut string (e.g., 'Ctrl+K', 'cmd+k')
 * @returns Normalized shortcut string
 */
export function normalizeShortcut(shortcut: string): string {
  const modifierOrder: string[] = ['ctrl', 'cmd', 'meta', 'shift', 'alt'];

  const parts: string[] = shortcut
    .toLowerCase()
    .split('+')
    .map((part: string) => part.trim())
    .filter((part: string) => part.length > 0);

  const modifiers: string[] = [];
  const others: string[] = [];

  for (const part of parts) {
    if (modifierOrder.includes(part)) {
      modifiers.push(part);
    } else {
      others.push(part);
    }
  }

  const orderedModifiers: string[] = modifierOrder.filter(
    (modifier: string) => modifiers.includes(modifier),
  );

  const orderedOthers: string[] = [...others].sort();

  return [...orderedModifiers, ...orderedOthers].join('+');
}

/**
 * Check if two shortcuts match
 * 
 * @param shortcut1 - First shortcut string
 * @param shortcut2 - Second shortcut string
 * @returns True if shortcuts match
 */
export function shortcutsMatch(shortcut1: string, shortcut2: string): boolean {
  const normalized1 = normalizeShortcut(
    shortcut1.replace(/cmd/g, 'ctrl').replace(/meta/g, 'ctrl')
  );
  const normalized2 = normalizeShortcut(
    shortcut2.replace(/cmd/g, 'ctrl').replace(/meta/g, 'ctrl')
  );
  
  return normalized1 === normalized2;
}

/**
 * Format shortcut for display based on platform
 * 
 * @param keys - Key combination string (e.g., 'ctrl+k')
 * @param platform - Target platform (defaults to current)
 * @returns Formatted shortcut string
 */
export function formatShortcut(keys: string, platform?: Platform): string {
  const targetPlatform = platform || detectPlatform();
  
  return keys
    .split('+')
    .map(key => {
      const lowerKey = key.toLowerCase();
      
      if (lowerKey in KEY_DISPLAY_NAMES) {
        const keyName = lowerKey as keyof typeof KEY_DISPLAY_NAMES;
        return KEY_DISPLAY_NAMES[keyName][targetPlatform];
      }
      
      return key.charAt(0).toUpperCase() + key.slice(1);
    })
    .join(targetPlatform === 'mac' ? '' : '+');
}

/**
 * Split formatted shortcut into individual keys for badge display
 * 
 * @param keys - Key combination string
 * @param platform - Target platform
 * @returns Array of individual key display strings
 */
export function splitShortcutKeys(keys: string, platform?: Platform): string[] {
  const targetPlatform = platform || detectPlatform();
  
  return keys
    .split('+')
    .map(key => {
      const lowerKey = key.toLowerCase();
      
      if (lowerKey in KEY_DISPLAY_NAMES) {
        const keyName = lowerKey as keyof typeof KEY_DISPLAY_NAMES;
        return KEY_DISPLAY_NAMES[keyName][targetPlatform];
      }
      
      return key.charAt(0).toUpperCase() + key.slice(1);
    });
}

/**
 * Check if element is an input that should prevent shortcuts
 * 
 * @param element - DOM element to check
 * @returns True if element is an input
 */
export function isInputElement(element: Element | null): boolean {
  if (!element || !element.tagName) {
    return false;
  }
  const tagName = element.tagName.toUpperCase();
  return INPUT_ELEMENT_TAGS.includes(tagName as typeof INPUT_ELEMENT_TAGS[number]);
}

/**
 * Check if keyboard event should be ignored for shortcuts
 * 
 * @param event - Keyboard event
 * @returns True if event should be ignored
 */
export function shouldIgnoreKeyboardEvent(event: KeyboardEvent): boolean {
  const target = event.target as Element | null;
  
  if (BYPASS_INPUT_CHECK_KEYS.includes(event.key as typeof BYPASS_INPUT_CHECK_KEYS[number])) {
    return false;
  }
  
  if (target && isInputElement(target)) {
    return true;
  }
  
  if (target && target.getAttribute && target.getAttribute('contenteditable') === 'true') {
    return true;
  }
  
  return false;
}

/**
 * Get keyboard shortcut for current platform
 * 
 * @param defaultKeys - Default key combination
 * @param platformKeys - Platform-specific overrides
 * @returns Key combination for current platform
 */
export function getPlatformShortcut(
  defaultKeys: string,
  platformKeys?: Partial<Record<Platform, string>>
): string {
  if (!platformKeys) {
    return defaultKeys;
  }
  
  const platform = detectPlatform();
  return platformKeys[platform] || defaultKeys;
}

/**
 * Create accessible label for keyboard shortcut
 * 
 * @param keys - Key combination string
 * @returns Accessible label string
 */
export function getShortcutAriaLabel(keys: string): string {
  const platform = detectPlatform();
  const parts = keys.split('+').map(key => {
    const lowerKey = key.toLowerCase();
    
    if (lowerKey === 'ctrl') return 'Control';
    if (lowerKey === 'cmd') return 'Command';
    if (lowerKey === 'shift') return 'Shift';
    if (lowerKey === 'alt') return platform === 'mac' ? 'Option' : 'Alt';
    if (lowerKey === 'meta') return platform === 'mac' ? 'Command' : 'Meta';
    
    return key.charAt(0).toUpperCase() + key.slice(1);
  });
  
  return parts.join(' + ');
}

/**
 * Check if keyboard shortcuts are supported in the current environment
 * 
 * @returns True if keyboard shortcuts are supported
 */
export function areKeyboardShortcutsSupported(): boolean {
  return typeof window !== 'undefined' && typeof window.addEventListener === 'function';
}
